// A delivery order never leaves without something to sign (2026-10-28, behind GET /features shipping_signing.delivery).
// SO-1144 PLT-QY1A shipped with the package guide's own "Ship pallet": no load, no sign request, no QR on the manifest.
//   * the package guide (office on the SO page, operator in the hub): a delivery order's Ship pallet / Ship all open
//     Load truck (./LoadTruckModal) for those pallets instead of the plain ship (deliveryLoadsOn, loadTruckOrderFromPlan);
//   * pallets that already shipped without a code: "Start delivery signing"
//     POST /packaging/plans/{plan_id}/delivery_signing {pin, pallet_ids?} (jobq-api: the pending code again when there
//     is one, else a new one) -> the link view {request_id, kind, url, short_code, qr_svg, expires_at, pallets,
//     existing}, shown in the signing panel (features/signing/SignRequestPanel), the same panel as Show signing QR.
// With the switch off nothing here is used: the old plain ship, no button.
import { startDeliverySigning } from '../signing/signRequestsApi';

/** True when this plan's ships go through Load truck: delivery signing on and an order we deliver. */
export function deliveryLoadsOn(plan, signing) {
  return !!(plan && signing && signing.delivery === true && plan.fulfillment_type === 'delivery');
}

/** The board-row shape LoadTruckModal reads, from a package guide plan: its Ready pallets (only `palletIds` when
 *  given: one pallet's Ship pallet). */
export function loadTruckOrderFromPlan(plan, palletIds = null) {
  const want = Array.isArray(palletIds) && palletIds.length ? new Set(palletIds.map(Number)) : null;
  const pallets = ((plan && plan.pallets) || [])
    .filter((p) => p.status === 'Ready' && (!want || want.has(Number(p.pallet_id))))
    .map((p) => ({
      pallet_id: p.pallet_id,
      short_id: p.short_id,
      pallet_num: p.pallet_num,
      status: p.status,
      beacon: p.beacon_id || p.beacon || null,
      pieces: p.pieces || null,
    }));
  return {
    plan_id: plan ? plan.plan_id : null,
    sales_order_id: plan ? plan.sales_order_id : null,
    order_number: plan ? plan.order_number : '',
    customer_name: plan ? plan.customer_name || null : null,
    fulfillment_type: plan ? plan.fulfillment_type : null,
    pallets,
  };
}

/** A delivery order's pallets that shipped and are not delivered yet (the package guide's plan: pod.delivered_at). */
export function deliveryWaitingPallets(plan) {
  return ((plan && plan.pallets) || []).filter((p) => p.status === 'Shipped' && !(p.pod && p.pod.delivered_at)
    && !p.delivered_at);
}

/** The hub's Out for delivery row: no live code yet (none, void or expired), so the button starts / shows one. */
export function needsDeliveryCode(order, signing) {
  if (!order || !signing || signing.delivery !== true || order.fulfillment_type !== 'delivery') return false;
  const s = order.sign;
  if (!s || s.request_id == null) return true;
  if (s.kind && s.kind !== 'delivery') return true;
  return s.status !== 'pending' && s.status !== 'signed';
}

async function openSignPanel(props) {
  const { signRequestPrompt } = await import('../signing/SignRequestPanel');
  return signRequestPrompt(props);
}

async function askWho(ui) {
  if (ui && typeof ui.identity === 'function') return ui.identity('load');
  return ui.pin('Enter your PIN to show the delivery signing QR.', { autofill: false });
}

/**
 * Start (or show again) the delivery signing QR for shipped pallets, then the signing panel.
 * -> {done, outcome, message}: done = the receiver signed while it was open; message = the sentence to show.
 */
export async function startDeliveryQr({ apiBase, ui, planId, orderNumber, customerName = null, palletIds = null,
  setBusy = () => {} }) {
  const pin = await askWho(ui);
  if (!pin) return { done: false, outcome: null, message: '' };
  setBusy(true);
  const r = await startDeliverySigning(apiBase, planId, { pin, palletIds });
  setBusy(false);
  if (!r.ok || !r.body) {
    return { done: false, outcome: null, message: `${orderNumber}: couldn't start delivery signing: ${r.message}` };
  }
  const outcome = await openSignPanel({
    apiBase, ui, request: r.body, kind: 'delivery', orderNumber,
    title: `Delivery · ${customerName || orderNumber}`,
    subtitle: orderNumber,
    canNewCode: false, canCancel: false,
  });
  if (outcome && outcome.status === 'signed') {
    const who = outcome.signed && outcome.signed.name ? ` by ${outcome.signed.name}` : '';
    return { done: true, outcome, message: `${orderNumber}: delivered · signed${who}.` };
  }
  return { done: false, outcome, message: '' };
}
